
/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useContext } from 'react';

import { Axis, Components, DIMENSION_STROKE_WIDTH, useSubscription, Vertex3 } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DIMENSION_FONT_SIZE, SYMBOL_ARROW_SIZE } from '@r-core/constants';
import { DimensionLine } from '@r-components';
import { DisplayMode } from '@r-core';
import { DrawEarDimension } from './draw-ear-dimension';
import { getDimensionMeasurement } from '@r-core/helpers';
import { NON_APPLIANCE_CATEGORY_KEYS } from '../../context-menu/helpers/is-swappable-appliance';
import { usePanelItem } from '@r-hooks';
import { type DimensionOptions, DimensionType, ShowDimensions } from '@r-core/types';

const SMALL_DIMENSION_THRESHOLD = 4;
const POP_OUT_OFFSET = 2;

// The passthrough marker is drawn tight to a 4"-wide cutout. Compactness comes from the short
// line-to-label gap and smaller arrowheads, NOT from shrinking the label -- the label should read
// at roughly the same size as the surrounding dimension text. At full spacing the marker stacks
// ~4.6" above a 2" cutout and collides with the vent gap dimensions above it.
const PASSTHROUGH_MARKER_LINE_OFFSET = -0.8;
const PASSTHROUGH_MARKER_FONT_FACTOR = 0.9;
const PASSTHROUGH_MARKER_ARROW_FACTOR = 0.75;
const PASSTHROUGH_MARKER_LABEL_GAP_FACTOR = 0.7;

export type DrawApplianceDimensionsType = {
	panelItemKey: string;
	isCountertop?: boolean;
}

export const DrawPanelItemDimensions = ({ panelItemKey, isCountertop = false }: DrawApplianceDimensionsType): React.ReactElement => {
	const { controller } = useContext(AppContext);
	// The panelItem can be mid-deletion when a subscription re-renders; getReferenceMap returns null for a removed id, so render nothing instead of crashing on the destructure.
	if (!controller.kitchen.getReferenceMap(panelItemKey)) return <></>;
	return <DrawPanelItemDimensionsContent panelItemKey={panelItemKey} isCountertop={isCountertop} />;
};

const DrawPanelItemDimensionsContent = ({ panelItemKey, isCountertop = false }: DrawApplianceDimensionsType): React.ReactElement => {
	const {
		controller
	} = useContext(AppContext);

	const { kitchen, options, navigation } = controller;

	const refMap = kitchen.getReferenceMap(panelItemKey);
	const { island, panel, panelItem, panelItemGroup } = refMap;
	const isHoleCategory = panelItem?.item?.category?.key === 'hole';
	const uPanelItem = usePanelItem(panelItem, { options, displayMode: navigation.displayMode });

	const counterRotation = panel.getOriginAngle() - island.axis.y;
	const viewportRotation = controller.viewport?.config?.rotation ?? 0;
	const isOverhead = navigation.displayMode === DisplayMode.Overhead || navigation.displayMode === DisplayMode.OverheadManufacturing;
	const parentRotation = isCountertop
		? isOverhead
			? viewportRotation + island.axis.y - panel.getOriginAngle()
			: viewportRotation - panel.getOriginAngle()
		: 0;

	const dimensionIslandAngle = isCountertop ? counterRotation : 0;

	useSubscription(controller, false, [['panel_item_dimension_updates', 'updated']]);

	const bpList = isCountertop ? island.getCountertop().breakpoints.children ?? [] : panel.breakpoints.children ?? [];
	const horizontalDimensionOptions: DimensionOptions[] = [];

	const horizontalCenterpointPrevWidth = panelItemGroup.getPreviousGroup(isCountertop) && panelItemGroup.getPreviousGroup(isCountertop).maxWidth;
	const horizontalCenterpointsPrev = panelItemGroup.getPreviousGroup(isCountertop) && panelItemGroup.getPreviousGroup(isCountertop).center.x;
	const distanceBetween = ((panelItemGroup.center.x - horizontalCenterpointsPrev) - ((panelItemGroup.maxWidth / 2) + (horizontalCenterpointPrevWidth / 2)));

	const earOverlap = horizontalCenterpointsPrev ? distanceBetween < 11 : false;

	let showDimensions = ShowDimensions.Outside;
	let { showAppliances, showCutouts } = options;
	let includeLabelBox = true;

	// Plain (catalog) cutouts/openings — not appliances, not hand-entered customs.
	const isPlainCutout = NON_APPLIANCE_CATEGORY_KEYS.has(panelItem?.item?.category?.key ?? '')
		&& panelItem?.item?.sku !== 'CUSTOM'
		&& !panelItem?.item?.override;

	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
		case DisplayMode.OverheadManufacturing:
		case DisplayMode.SkuOverheadManufacturing:
			showAppliances = false;
			showCutouts = true;
			showDimensions = ShowDimensions.Inside;
			includeLabelBox = false;
			break;
		case DisplayMode.SkuOverhead:
			showAppliances = false;
			showCutouts = isPlainCutout;
			if (isPlainCutout) showDimensions = ShowDimensions.Inside;
			break;
		default:
			showDimensions = options.showAppliances && options.showCutouts
				? options.showDimensionsMode
				: options.showAppliances
					? ShowDimensions.Outside
					: ShowDimensions.Inside;
			break;
	}

	if (options.showDimensionsMode !== ShowDimensions.None && options.showApplianceDimensions &&
		((showAppliances && navigation.displayMode !== DisplayMode.Manufacturing && navigation.displayMode !== DisplayMode.InlineManufacturing) || showCutouts)) {
		// don't show hint dimensions unless enabled through debug options
		const isSpacer = panelItem.item.category?.isSpacer ?? false;
		// Spacers only show dimensions in the view where they're visible (front-only on panel, top-only on countertop)
		if (isSpacer && uPanelItem._item.showHint && !isCountertop) return null;
		if (uPanelItem._item.showHint && !controller.options.showHints && !isSpacer) return null;
		// TOP_ONLY items with no below-counter extent (pizza ovens, asado smokers, etc.) have no
		// panel-face cutout to dimension on an MFG sheet -- the SKU label above the panel is the
		// only meaningful content there. Sinks (TOP_ONLY + distanceBelowCountertop > 0) still fall
		// through to draw their below-counter cutout dims.
		if (!isCountertop
			&& (navigation.displayMode === DisplayMode.Manufacturing || navigation.displayMode === DisplayMode.InlineManufacturing)
			&& panelItem.item.placement === Components.PLACEMENT_TOP_ONLY
			&& !panelItem.item.distanceBelowCountertop) return null;

		// use appliance or cutout dimensions based on the showDimensions setting
		const piSource = showDimensions === ShowDimensions.Inside
			? uPanelItem._cutout
			: uPanelItem._item;
		const hw = piSource.size.width / 2;

		// build a list of breakpoints that overlap with the panelitem (spacers ignore breakpoints)
		const breakpoints = isSpacer ? [] : bpList.filter((bp) => {
			const leftEdge = panelItemGroup.center.x - hw;
			const rightEdge = panelItemGroup.center.x + hw;
			const containsBreakpoint = bp.center > leftEdge && bp.center < rightEdge;
			return containsBreakpoint;
		});

		const dimensionOptions: DimensionOptions = {
			fontSize: DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor,
			indicatorEnd: 'arrow',
			indicatorStart: 'arrow',
			type: DimensionType.Inside,
			useFractions: options.useFractions,
			height: isCountertop
				? showDimensions === ShowDimensions.Inside && panelItem.item.placement !== Components.PLACEMENT_TOP_ONLY
					? piSource.size.depth + island.panels.getFrontPanel().countertopOverhang
					: piSource.size.depth
				: Math.min(piSource.size.height - (uPanelItem._item.showHint ? panelItem.item.distanceAboveCountertop : 0), panel.size.height),
			width: piSource.size.width,
			xOffset: 0,
			yOffset: isCountertop
				? -panelItem.center.z
				: panelItem.center.y + (uPanelItem._item.showHint ? panelItem.item.distanceAboveCountertop : 0),
			xPositionFactor: panelItem.dimensionOffsets.vLine,
			yPositionFactor: panelItem.dimensionOffsets.hLine,
			xTextFactor: panelItem.dimensionOffsets.hLabel,
			yTextFactor: panelItem.dimensionOffsets.vLabel
		};

		/* split dimensions at breakpoints */
		if (breakpoints.length > 0) {
			let lastX = panelItemGroup.center.x - hw;
			let lastWidth = 0;
			// create a dimension item for each segment within the breakpoint
			breakpoints.forEach((bp) => {
				horizontalDimensionOptions.push({
					...dimensionOptions,
					xOffset: ((bp.center - lastX) - piSource.size.width + (lastWidth * 2)) / 2,
					width: bp.center - lastX
				});
				lastWidth = bp.center - lastX;
				lastX = bp.center;
			});

			// inject the last breakpoint dimension segment
			horizontalDimensionOptions.push({
				...dimensionOptions,
				xOffset: Math.abs(((lastX - (panelItemGroup.center.x + hw)) + piSource.size.width) / 2),
				// yOffset: dimensionOptions.yOffset - (panelItem.item.component.dimensions.inside.horizontal.offset ?? 0),
				width: (piSource.size.width / 2) + panelItemGroup.center.x - lastX
			});
		} else {
			horizontalDimensionOptions.push({
				...dimensionOptions
			});
		}

		const originOffset = new Vertex3({ dataIn: {
			x: 0,
			y: 0,
			z: panelItem.item.distanceFrontOfFace / 2
			// z: (-uPanelItem._item.offset.z + piSource.offset.z) / 2
		}});

		// adjust custout offset to account for frontOfFace !== overhang
		if (isCountertop && showDimensions === ShowDimensions.Inside) {
			const d = panelItem.item.size.depth - panelItem.item.distanceFrontOfFace - panelItem.item.cutout.depth;
			if (panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP) {
				originOffset.z = (panel.countertopOverhang + d) / 2;
			} else if (panelItem.item.placement === Components.PLACEMENT_TOP_ONLY) {
				originOffset.z = d / 2;
			}
		}

		if (panelItem.sleeve !== null) {
			const sleeveChild = panelItemGroup.items.children.find((i) => i.item.component.sleeveDepth > 0 || i.item.category?.isInsulatedJacket);
			if (sleeveChild) {
				piSource.origin.y = (-(sleeveChild.item.component.sleeveDepth ?? 0) / 2) + 2;
			}
		}

		// Suffix the persist sourceId for countertop view so overhead and front panel
		// dimensions resolve to separate Dimension objects (independent customAngle, lineFactor, etc.)
		const persistSourceId = isCountertop ? `${panelItem.id}:ct` : panelItem.id;

		const hLabelOffset = isCountertop ? panelItem.dimensionOffsets.zLabel : panelItem.dimensionOffsets.hLabel;
		const hLineOffset = isCountertop ? panelItem.dimensionOffsets.zLine : panelItem.dimensionOffsets.hLine;
		const isPassthroughMarker = panelItem.item.sku?.includes('PASSTHROUGH') ?? false;
		const horizontalDimOffset = panelItem.item.component.dimensions.inside.horizontal.offset
			|| (isPassthroughMarker ? PASSTHROUGH_MARKER_LINE_OFFSET : 0);


		const frontOverhang = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY
			? 0
			: panel.countertopOverhang;

		// Re-apply panelItem offset since dims are rendered outside the panelItem <g>
		const countertopTransform = isCountertop ? `translate(${panelItem.center.x}, ${panelItem.center.z})` : undefined;
		const overheadTransform = isOverhead ? `translate(${panelItem.center.x}, ${panelItem.center.z})` : countertopTransform;

		// Pop-out padding: when dimension is too small to fit inside the cutout, render it outside.
		// Cutout-category items on the base panel get a separate "inside-the-edge" placement --
		// without it, the default vLine/hLine factors put both dim lines through the cutout's
		// center and the horizontal/vertical labels stack on each other. We park the lines just
		// inside opposite edges of the cutout with centered labels instead.
		const isCutoutCategory = panelItem.item?.category?.key === 'cutout';
		const forceCutoutInsideEdge = isCutoutCategory && !isCountertop;
		const isVerticalPopOut = dimensionOptions.height <= SMALL_DIMENSION_THRESHOLD;
		const verticalPadding = isVerticalPopOut
			? (piSource.size.width / 2) + POP_OUT_OFFSET
			: forceCutoutInsideEdge
				? (piSource.size.width / 2) - POP_OUT_OFFSET
				: (piSource.size.width / 2) - (panelItem.dimensionOffsets.vLine * piSource.size.width);
		const verticalLabelFactor = isVerticalPopOut || forceCutoutInsideEdge ? 0.5 : panelItem.dimensionOffsets.vLabel;

		return (
			<g transform={overheadTransform}>
				{ (!showAppliances && showCutouts && uPanelItem._item.showHint && !isCountertop) || panelItem.sleeve !== null
					? ''
					: !isHoleCategory && uPanelItem._dimensions.inside.vertical.render &&
					(showAppliances
						? <DimensionLine
							length={dimensionOptions.height}
							persist={{ sourceId: persistSourceId, targetId: panelItemGroup.id }}
							bBox={{
								origin: new Vertex3({ dataIn: { x: 0, y: panelItem.getCenter(!showAppliances).y, z: isCountertop ? originOffset.z : panelItem.item.distanceFrontOfFace / 2 }}),
								size: { width: panelItem.size.width, height: panelItem.size.height, depth: panelItem.size.depth }
							}}
							axis={new Axis({ dataIn: { z: 90, y: 90 }})}
							includeLabelBox={includeLabelBox}
							canMove={true}
							labelFactor={verticalLabelFactor}
							padding={verticalPadding}
							indicatorStart="arrow" indicatorEnd="arrow"
							topView={isCountertop}
							islandAngle={dimensionIslandAngle}
							parentRotation={parentRotation}
						/>
						: !panelItem.isStandAlone && <DimensionLine
							length={dimensionOptions.height}
							persist={{ sourceId: persistSourceId, targetId: panelItemGroup.id }}
							bBox={{
								origin: new Vertex3({ dataIn: {
									y: panelItem.getCenter(!showAppliances).y,
									z: isCountertop
										? originOffset.z
										: (panelItem.size.depth - panelItem.cutout.depth - panelItem.item.distanceFrontOfFace + frontOverhang) / 2
								}}),
								size: { width: panelItem.size.width, height: panelItem.size.height, depth: panelItem.size.depth }
							}}
							axis={new Axis({ dataIn: { z: 90, y: 90 }})}
							includeLabelBox={includeLabelBox}
							canMove={true}
							labelFactor={verticalLabelFactor}
							padding={verticalPadding}
							indicatorStart="arrow" indicatorEnd="arrow"
							topView={isCountertop}
							islandAngle={dimensionIslandAngle}
							parentRotation={parentRotation}
						/>
					)
				}

				{ (!showAppliances && showCutouts && uPanelItem._item.showHint && !isCountertop) || panelItem.sleeve !== null
					? ''
					: !isHoleCategory && uPanelItem._dimensions.inside.horizontal.render &&
					horizontalDimensionOptions.map((hd, i) => {
						// Pass-through offset dimension: render simple SVG line with outward arrows above the cutout
						if (horizontalDimOffset && !isCountertop) {
							const dimY = (panelItem.getCenter(!showAppliances).y)
								+ horizontalDimOffset - (hd.height / 2);
							const halfW = hd.width / 2;
							// the arrow symbols have no viewBox, so width/height cannot scale them -- a
							// transform is the only way to shrink them to match the compact marker
							const arrowScale = isPassthroughMarker ? PASSTHROUGH_MARKER_ARROW_FACTOR : 1;
							const arrowSize = SYMBOL_ARROW_SIZE * arrowScale;
							const halfArrow = arrowSize / 2;
							const fontSize = DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor *
								(isPassthroughMarker ? PASSTHROUGH_MARKER_FONT_FACTOR : 1);
							const labelY = dimY - (fontSize * (isPassthroughMarker ? PASSTHROUGH_MARKER_LABEL_GAP_FACTOR : 1));
							const label = getDimensionMeasurement(hd.width, options.useFractions);
							// stop the line at the arrow bases, not the tips -- the apex is a zero-width
							// point while the stroke has real thickness, so a line ending at the tip
							// leaves a visible stub sticking out past the arrowhead
							const lineHalfW = Math.max(0, halfW - arrowSize);
							return (
								<g key={i}>
									<line x1={-lineHalfW} y1={dimY} x2={lineHalfW} y2={dimY}
										stroke="black" strokeWidth={DIMENSION_STROKE_WIDTH} />
									<use xlinkHref="#left-arrow" transform={`translate(${-halfW} ${dimY - halfArrow}) scale(${arrowScale})`} />
									<use xlinkHref="#right-arrow" transform={`translate(${halfW - arrowSize} ${dimY - halfArrow}) scale(${arrowScale})`} />
									{includeLabelBox && <rect x={-fontSize * 0.75} y={labelY - fontSize * 0.6} width={fontSize * 1.5} height={fontSize * 1.2}
										fill="white" stroke="black" strokeWidth={DIMENSION_STROKE_WIDTH / 3} rx="0.5" ry="0.75" opacity={0.7} />}
									<text x={0} y={labelY} textAnchor="middle" dominantBaseline="middle"
										fill="black" fontSize={fontSize} className="rta-designer-component-measure rta-noselect">
										{label}&quot;
									</text>
								</g>
							);
						}

						// Pop-out padding for small horizontal dimensions; cutout-category items on
						// the base panel get an "inside-the-edge" placement (see vertical above) so
						// their horizontal/vertical labels don't collide at the cutout center.
						const isHorizontalPopOut = hd.width <= SMALL_DIMENSION_THRESHOLD;
						const horizontalPadding = isHorizontalPopOut
							? isCountertop
								? -((piSource.size.depth / 2) + POP_OUT_OFFSET)
								: -((hd.height / 2) + POP_OUT_OFFSET)
							: forceCutoutInsideEdge
								? -((hd.height / 2) - POP_OUT_OFFSET)
								: isCountertop
									? (piSource.size.depth / 2) - (hLineOffset * piSource.size.depth)
									: (hd.height / 2) - (hLineOffset * hd.height);
						const horizontalLabelFactor = isHorizontalPopOut || forceCutoutInsideEdge ? 0.5 : hLabelOffset;

						return (
							showAppliances
								? <DimensionLine key={i}
									length={hd.width}
									persist={{ sourceId: persistSourceId, targetId: panelItemGroup.id }}
									bBox={{
										origin: new Vertex3({ dataIn: {
											x: hd.xOffset,
											y: panelItem.getCenter(!showAppliances).y,
											z: isCountertop ? originOffset.z : 0
										}}),
										size: { width: hd.width, height: hd.height, depth: piSource.size.depth }
									}}
									axis={new Axis({ dataIn: { z: 0 }})}
									includeLabelBox={includeLabelBox}
									canMove={true}
									labelFactor={horizontalLabelFactor}
									padding={horizontalPadding}
									indicatorStart="arrow" indicatorEnd="arrow"
									topView={isCountertop}
									islandAngle={dimensionIslandAngle}
									parentRotation={parentRotation}
								/>
								: !panelItem.isStandAlone && <DimensionLine key={i}
									length={hd.width}
									persist={{ sourceId: persistSourceId, targetId: panelItemGroup.id }}
									bBox={{
										origin: new Vertex3({ dataIn: {
											x: hd.xOffset,
											y: panelItem.getCenter(!showAppliances).y,
											z: isCountertop ? originOffset.z : 0
										}}),
										size: { width: hd.width, height: hd.height, depth: piSource.size.depth }
									}}
									axis={new Axis({ dataIn: { z: 0 }})}
									includeLabelBox={includeLabelBox}
									canMove={true}
									labelFactor={horizontalLabelFactor}
									padding={horizontalPadding}
									indicatorStart="arrow" indicatorEnd="arrow"
									topView={isCountertop}
									islandAngle={dimensionIslandAngle}
									parentRotation={parentRotation}
								/>
						);
					})
				}

				{ /* Remove Ear Dims from SKU Layer - hide ear dimensions when SKU layer is active */}
				{ isCountertop && panelItem.hasEars && navigation.displayMode !== DisplayMode.SkuOverhead && navigation.displayMode !== DisplayMode.SkuOverheadManufacturing &&
					<DrawEarDimension panelItem={panelItem} options={options} earOverlap={earOverlap}/>
				}
			</g>
		);
	}

	return null;
};
